import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, Gauge, ChevronRight } from "lucide-react";
import Shell from "@/components/Shell";
import DriftRadar from "@/components/flow/DriftRadar";
import { http, MODE_TINT } from "@/lib/api";
import { useApp } from "@/lib/store";
import { toast } from "sonner";

const HELP_LABEL = {
  yes_noticeably: "Helped noticeably",
  a_little: "Helped a little",
  no_difference: "No clear difference",
  made_harder: "Made things harder",
  could_not_tell: "Could not tell",
};

export default function History() {
  const { deviceId, refreshProgress } = useApp();
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    http.get(`/history/${deviceId}`).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  };
  useEffect(load, [deviceId]);

  const del = async (id) => {
    try {
      await http.delete(`/checkin/${id}`);
      setItems((it) => it.filter((x) => x.id !== id));
      refreshProgress();
      toast("Entry deleted");
    } catch (e) { toast("Could not delete"); }
  };

  const modes = ["all", "CLEAR", "SETTLE", "RESTORE", "PROTECT", "RECONNECT", "MOBILIZE"];
  const filtered = (items || []).filter((i) => filter === "all" || i.mode === filter);

  return (
    <Shell>
      <div className="px-6 pt-14 pb-28">
        <h1 className="font-serif-display text-4xl">History</h1>
        <p className="mt-2 text-[14px] text-[#474747]">A record of your uses. No judgment, no streaks.</p>

        <DriftRadar className="mt-5" />

        <button data-testid="lr-entry" onClick={() => nav("/load-recovery")}
          className="mt-3 w-full ss-card p-4 flex items-center gap-3 text-left ss-focus hover:-translate-y-0.5 transition-transform">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[#F5F3ED] flex-shrink-0">
            <Gauge size={17} strokeWidth={1.6} className="text-[#C9A227]" />
          </span>
          <div className="flex-1">
            <div className="text-[14px] font-medium">Load &amp; Recovery</div>
            <div className="text-[12.5px] text-[#6B6B6B]">Your recovery balance and load type over time</div>
          </div>
          <ChevronRight size={18} className="text-[#9a9a94]" />
        </button>

        <div className="mt-5 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {modes.map((m) => (
            <button
              key={m}
              data-testid={`history-filter-${m}`}
              onClick={() => setFilter(m)}
              className={`whitespace-nowrap rounded-full px-3.5 py-2 text-[12.5px] border ss-tap ss-focus ${
                filter === m ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "border-[#EBE9E4] bg-white text-[#474747]"
              }`}
            >
              {m === "all" ? "All" : m}
            </button>
          ))}
        </div>

        {items === null ? (
          <p className="mt-10 text-[#6B6B6B]">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="mt-16 text-center">
            <p className="font-serif-display text-2xl text-[#474747]">Nothing here yet.</p>
            <p className="mt-2 text-[14px] text-[#6B6B6B]">Come back when something feels off.</p>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            {filtered.map((it, i) => (
              <motion.div
                key={it.id}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.02 * i }}
                className="ss-card p-4 flex items-start justify-between gap-3"
                data-testid="history-item"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: it.is_baseline ? "#C9A227" : (MODE_TINT[it.mode] || "#EBE9E4") }} />
                    <span className="text-[15px] font-semibold">
                      {it.is_baseline ? "Baseline" : it.mode_label}
                    </span>
                    <span className="text-[11px] text-[#6B6B6B] font-mono-ss">
                      {new Date(it.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                    </span>
                  </div>
                  {it.demand_label && <p className="mt-1 text-[13px] text-[#474747]">{it.demand_label}</p>}
                  {it.protocol_title && <p className="mt-0.5 text-[13px] text-[#6B6B6B]">{it.protocol_title}</p>}
                  {it.helpfulness && (
                    <p className="mt-1 text-[12px] text-[#C9A227]">{HELP_LABEL[it.helpfulness]}</p>
                  )}
                </div>
                <button
                  data-testid={`history-delete-${it.id}`}
                  onClick={() => del(it.id)}
                  className="p-2 text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus"
                  aria-label="Delete entry"
                >
                  <Trash2 size={17} strokeWidth={1.5} />
                </button>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Shell>
  );
}
